import {parsePdfInkAnnotations,type PdfInkAnnotation,type PdfInkDocument,type PdfInkPage,type PdfInkPageUpdate} from '@reados/contracts';
import {request,ApiError} from './api';

export type PdfInkStatus={status:'loading'|'saved'|'saving'|'pending'|'conflict'|'error';message?:string};
export interface PdfInkSessionOptions {
  userId:string;fileId:string;contentRevision:string;
  onStatus:(state:PdfInkStatus)=>void;
  onRemotePage?:(pageIndex:number,annotations:PdfInkAnnotation[])=>void;
}
export interface PdfInkDraft {
  key:string;identity:string;pageIndex:number;baseRevision:number;annotations:PdfInkAnnotation[];
  pending:PdfInkPageUpdate|null;updatedAt:number;archived?:boolean;
}
export interface PdfInkDraftStore {
  list(identity:string):Promise<PdfInkDraft[]>;put(draft:PdfInkDraft):Promise<void>;remove(key:string):Promise<void>;
  /** Atomically compare the old row before archiving/removing it. Another tab may still own it. */
  recover(previous:PdfInkDraft,replacement:PdfInkDraft|null):Promise<boolean>;
}
export interface PdfInkTransport {load():Promise<PdfInkDocument>;save(pageIndex:number,update:PdfInkPageUpdate):Promise<PdfInkPage>}

let database:Promise<IDBDatabase>|undefined;
function db():Promise<IDBDatabase>{
  database??=new Promise((resolve,reject)=>{
    if(typeof indexedDB==='undefined'){reject(new Error('浏览器无法保存手写草稿，请使用普通浏览模式。'));return;}
    const open=indexedDB.open('reados-pdf-ink-v1',1);
    open.onupgradeneeded=()=>{const store=open.result.createObjectStore('drafts',{keyPath:'key'});store.createIndex('identity','identity');};
    open.onsuccess=()=>{open.result.onversionchange=()=>{open.result.close();database=undefined;};resolve(open.result);};
    open.onerror=()=>{database=undefined;reject(open.error);};
    open.onblocked=()=>{database=undefined;reject(new Error('手写草稿存储被另一个标签页占用，请关闭旧标签后重试。'));};
  });return database;
}
async function transaction<T>(mode:IDBTransactionMode,action:(store:IDBObjectStore)=>IDBRequest<T>):Promise<T>{
  const connection=await db();return new Promise((resolve,reject)=>{
    const tx=connection.transaction('drafts',mode);const result=action(tx.objectStore('drafts'));
    tx.oncomplete=()=>resolve(result.result);tx.onerror=()=>reject(tx.error||result.error);tx.onabort=()=>reject(tx.error||new Error('手写草稿保存被中断'));
  });
}
const drafts:PdfInkDraftStore={
  list:identity=>transaction('readonly',s=>s.index('identity').getAll(identity)),
  put:async value=>{await transaction('readwrite',s=>s.put(value));},
  remove:async key=>{await transaction('readwrite',s=>s.delete(key));},
  recover:async(previous,replacement)=>{
    const connection=await db();return new Promise((resolve,reject)=>{
      const tx=connection.transaction('drafts','readwrite'),store=tx.objectStore('drafts'),read=store.get(previous.key);let matched=false;
      read.onsuccess=()=>{if(!equal(read.result,previous))return;matched=true;if(replacement){store.put(replacement);store.put({...previous,archived:true});}else store.delete(previous.key);};
      tx.oncomplete=()=>resolve(matched);tx.onerror=()=>reject(tx.error);tx.onabort=()=>reject(tx.error||new Error('草稿恢复被中断'));
    });
  }
};
const equal=(a:unknown,b:unknown)=>JSON.stringify(a)===JSON.stringify(b);
const copy=<T,>(value:T):T=>structuredClone(value);

/** One shared core per account/document/version in this tab. Requests and IDB writes
 * are serialized; an acknowledged request only consumes its exact snapshot.
 */
export class PdfInkSessionCore {
  readonly listeners=new Set<Pick<PdfInkSessionOptions,'onStatus'|'onRemotePage'>>();
  readonly identity:string;
  private readonly clientId=crypto.randomUUID();
  private pages=new Map<number,PdfInkPage>();
  private dirty=new Map<number,PdfInkDraft>();
  private recovered:PdfInkDraft[]=[];
  private status:PdfInkStatus={status:'loading'};
  private loading?:Promise<PdfInkDocument>;
  private saving?:Promise<void>;
  private localWrites:Promise<void>=Promise.resolve();
  private readonly localErrors=new Map<string,unknown>();
  private timer?:ReturnType<typeof setTimeout>;
  private loaded=false;
  private conflict=false;
  constructor(readonly options:Pick<PdfInkSessionOptions,'userId'|'fileId'|'contentRevision'>,private transport:PdfInkTransport,private draftStore:PdfInkDraftStore=drafts){
    this.identity=JSON.stringify([options.userId,options.fileId,options.contentRevision]);
  }
  subscribe(listener:Pick<PdfInkSessionOptions,'onStatus'|'onRemotePage'>){this.listeners.add(listener);listener.onStatus(this.status);return()=>this.listeners.delete(listener);}
  private report(state:PdfInkStatus){
    if(this.localErrors.size&&['saved','pending','saving'].includes(state.status))state={status:'error',message:'部分笔迹无法写入浏览器草稿，请保持页面打开并下载恢复数据。'};
    this.status=state;for(const listener of this.listeners)listener.onStatus(state);
  }
  private broadcast(index:number,annotations:PdfInkAnnotation[],except?:unknown){for(const listener of this.listeners)if(listener!==except)listener.onRemotePage?.(index,copy(annotations));}
  get annotations(){const pages=new Map([...this.pages].map(([i,p])=>[i,p.annotations]));for(const [i,d] of this.dirty)pages.set(i,d.annotations);return [...pages].sort(([a],[b])=>a-b).flatMap(([,a])=>copy(a));}
  get recoveryData(){return JSON.stringify({schemaVersion:1,fileId:this.options.fileId,contentRevision:this.options.contentRevision,pages:this.annotations,drafts:[...this.recovered,...this.dirty.values()]},null,2);}
  get hasRecovery(){return this.recovered.length>0||this.dirty.size>0;}
  async loadRecovery(){this.recovered=await this.draftStore.list(this.identity);}
  private snapshot():PdfInkDocument{return {schemaVersion:1,fileId:this.options.fileId,contentRevision:this.options.contentRevision,pages:[...new Set([...this.pages.keys(),...this.dirty.keys()])].sort((a,b)=>a-b).map(i=>({...this.page(i),annotations:copy(this.dirty.get(i)?.annotations??this.page(i).annotations)}))};}
  private page(index:number):PdfInkPage{return this.pages.get(index)??{pageIndex:index,revision:0,mutationId:null,annotations:[],updatedAt:null};}
  private verify(document:PdfInkDocument){if(document.schemaVersion!==1||document.fileId!==this.options.fileId||document.contentRevision!==this.options.contentRevision)throw new Error('PDF 来源版本不一致，请重新打开文件。');for(const page of document.pages)parsePdfInkAnnotations(page.annotations,page.pageIndex);}
  load():Promise<PdfInkDocument>{
    if(this.loaded)return Promise.resolve(this.snapshot());
    this.loading??=this.loadOnce().catch(error=>{this.loading=undefined;this.report({status:'error',message:message(error)});throw error;});return this.loading;
  }
  private async loadOnce(){
    this.report({status:'loading'});
    // Recovery remains downloadable when the server is unavailable. This does not
    // enable editing until the owned PDF source and server version are verified.
    const stored=await this.draftStore.list(this.identity);this.recovered=stored;
    const document=await this.transport.load();this.verify(document);
    this.recovered=stored.filter(d=>d.archived);
    this.pages=new Map(document.pages.map(p=>[p.pageIndex,p]));
    for(const row of stored.filter(d=>!d.archived).sort((a,b)=>a.updatedAt-b.updatedAt)){
      parsePdfInkAnnotations(row.annotations,row.pageIndex);
      const remote=this.page(row.pageIndex),draft=copy(row);
      if(draft.pending&&remote.mutationId===draft.pending.mutationId&&equal(remote.annotations,draft.pending.annotations)){
        draft.baseRevision=remote.revision;draft.pending=null;
      }
      if(equal(remote.annotations,draft.annotations)){if(!await this.draftStore.recover(row,null))this.conflict=true;continue;}
      if(this.dirty.has(draft.pageIndex)&&!equal(this.dirty.get(draft.pageIndex)!.annotations,draft.annotations))this.conflict=true;
      if(draft.baseRevision!==remote.revision)this.conflict=true;
      // Never reuse a previous browser instance's key: its in-flight acknowledgement
      // can still remove that key after this instance has recorded newer strokes.
      draft.key=`${this.identity}:${this.clientId}:${draft.pageIndex}`;
      if(!await this.draftStore.recover(row,draft))this.conflict=true;
      this.recovered.push(copy(row));this.dirty.set(draft.pageIndex,draft);
    }
    this.loaded=true;
    this.report(this.conflict?{status:'conflict',message:'批注存在其他版本，本地笔迹已保留，请选择恢复方式。'}:this.dirty.size?{status:'pending',message:'已恢复本地草稿，等待同步。'}:{status:'saved'});
    if(this.dirty.size&&!this.conflict)this.schedule();
    return this.snapshot();
  }
  pageChanged(index:number,annotations:PdfInkAnnotation[],origin?:unknown){
    if(!this.loaded)throw new Error('手写批注尚未加载完成');
    const validated=parsePdfInkAnnotations(annotations,index);
    const previous=this.dirty.get(index),base=this.page(index);
    if(equal(previous?.annotations??base.annotations,validated))return;
    const draft:PdfInkDraft=previous?{...previous,annotations:validated,updatedAt:Date.now()}:{key:`${this.identity}:${this.clientId}:${index}`,identity:this.identity,pageIndex:index,baseRevision:base.revision,annotations:validated,pending:null,updatedAt:Date.now()};
    this.dirty.set(index,draft);this.persist(draft);this.broadcast(index,validated,origin);
    this.report(this.conflict?{status:'conflict',message:'本地笔迹已保留，云端版本冲突尚未解决。'}:{status:'pending',message:'正在保存草稿…'});this.schedule();
  }
  private persist(draft:PdfInkDraft){const captured=copy(draft);this.localWrites=this.localWrites.catch(()=>{}).then(()=>this.draftStore.put(captured)).then(()=>{this.localErrors.delete(captured.key);}).catch(error=>{this.localErrors.set(captured.key,error);this.report({status:'error',message:'浏览器草稿保存失败，请保持页面打开并下载恢复数据。'});});}
  private schedule(){clearTimeout(this.timer);if(!this.conflict)this.timer=setTimeout(()=>{void this.flush().catch(()=>{});},600);}
  flush():Promise<void>{
    clearTimeout(this.timer);
    if(this.saving)return this.saving;
    if(!this.loaded)return Promise.resolve();
    if(this.conflict)return Promise.reject(new Error('手写批注版本冲突，请先下载恢复数据或选择云端版本。'));
    this.saving=this.saveLoop().finally(()=>{this.saving=undefined;});return this.saving;
  }
  private async saveLoop(){
    try{
      while(this.dirty.size){
        const [index,draft]=this.dirty.entries().next().value!;
        if(!draft.pending){draft.pending={schemaVersion:1,contentRevision:this.options.contentRevision,baseRevision:draft.baseRevision,mutationId:crypto.randomUUID(),annotations:copy(draft.annotations)};this.persist(draft);}
        await this.localWrites;if(this.localErrors.size)throw this.localErrors.values().next().value;
        const update=copy(draft.pending);this.report({status:'saving'});
        const result=await this.transport.save(index,update);
        if(result.pageIndex!==index||result.mutationId!==update.mutationId||result.revision!==update.baseRevision+1||!equal(result.annotations,update.annotations))throw new Error('服务器未确认本次手写保存，请重试。');
        this.pages.set(index,result);
        const latest=this.dirty.get(index)!;
        if(equal(latest.annotations,update.annotations)){
          await this.localWrites;
          // A newer stroke can arrive while IndexedDB is committing.
          if(equal(this.dirty.get(index)?.annotations,update.annotations)){
            this.dirty.delete(index);
            this.localWrites=this.localWrites.then(()=>this.draftStore.remove(latest.key));
            await this.localWrites;
          }else{const newer=this.dirty.get(index)!;newer.baseRevision=result.revision;newer.pending=null;this.persist(newer);}
        }else{latest.baseRevision=result.revision;latest.pending=null;this.persist(latest);}
      }
      this.report({status:'saved'});
    }catch(error){
      if(error instanceof ApiError&&(error.code==='PDF_INK_CONFLICT'||error.code==='PDF_INK_SOURCE_CHANGED'||error.status===404)){
        this.conflict=true;this.report({status:'conflict',message:message(error)});
      }else if(!this.localErrors.size&&(error instanceof TypeError||error instanceof ApiError&&error.status>=500)){
        this.report({status:'pending',message:'笔迹已保存在此设备，连接恢复后将继续同步。'});
      }else this.report({status:'error',message:`尚未同步：${message(error)}。请重试，或下载恢复数据。`});
      throw error;
    }
  }
  async retry(){if(!this.loaded){await this.load();return;}for(const draft of this.dirty.values())this.persist(draft);await this.flush();}
  async useServerVersion(){
    clearTimeout(this.timer);if(this.saving)await this.saving.catch(()=>{});await this.localWrites;
    const document=await this.transport.load();this.verify(document);
    const previous=new Set([...this.pages.keys(),...this.dirty.keys()]);
    // Keep every displaced local version recoverable, including alternate-tab drafts.
    for(const row of this.dirty.values()){
      const archived={...row,archived:true};
      if(!await this.draftStore.recover(row,archived))throw new Error('恢复期间本地笔迹又发生变化，请重新读取后处理。');
      this.recovered.push(archived);
    }
    this.dirty.clear();this.pages=new Map(document.pages.map(p=>[p.pageIndex,p]));this.conflict=false;this.localErrors.clear();
    for(const index of new Set([...previous,...this.pages.keys()]))this.broadcast(index,this.page(index).annotations);
    this.report({status:'saved'});
  }
  async detach(){clearTimeout(this.timer);await this.flush().catch(()=>{});await this.localWrites;}
}
const cores=new Map<string,PdfInkSessionCore>();
const message=(error:unknown)=>error instanceof Error?error.message:'连接中断';

export class PdfInkSession {
  private core:PdfInkSessionCore;
  private unsubscribe:()=>void;
  private online=()=>{void this.retry().catch(()=>{});};
  constructor(private options:PdfInkSessionOptions){
    const key=JSON.stringify([options.userId,options.fileId,options.contentRevision]);
    const path=`/files/${encodeURIComponent(options.fileId)}/ink`;
    let core=cores.get(key);
    if(!core){core=new PdfInkSessionCore(options,{load:()=>request(`${path}?contentRevision=${options.contentRevision}`,{cache:'no-store'}),save:(index,update)=>request(`${path}/pages/${index}`,{method:'PUT',body:JSON.stringify(update)})});cores.set(key,core);}
    this.core=core;this.unsubscribe=core.subscribe(options);window.addEventListener('online',this.online);
  }
  load(){return this.core.load();}
  loadRecovery(){return this.core.loadRecovery();}
  pageChanged(index:number,annotations:PdfInkAnnotation[]){this.core.pageChanged(index,annotations,this.options);}
  get annotations(){return this.core.annotations;}
  get recoveryData(){return this.core.recoveryData;}
  get hasRecovery(){return this.core.hasRecovery;}
  flush(){return this.core.flush();}
  retry(){return this.core.retry();}
  useServerVersion(){return this.core.useServerVersion();}
  destroy(){
    this.unsubscribe();window.removeEventListener('online',this.online);
    if(!this.core.listeners.size)void this.core.detach().finally(()=>{
      if(!this.core.listeners.size&&cores.get(this.core.identity)===this.core)cores.delete(this.core.identity);
    });
  }
}
